/* eslint-disable react/no-unescaped-entities */
import React from 'react'
import { useForm } from '@mantine/form'
import { useListState } from '@mantine/hooks'
import { IconSearch } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { zodResolver } from 'mantine-form-zod-resolver'
import { Button, Modal, Text } from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'

import {
  WarehouseToolBaseType,
  CreateWarehouseToolType,
  UpdateWarehouseToolType,
  CreateWarehouseToolSchema,
} from '@customTypes/warehouseTool'

import DataRepo from '@api/datasource/data'
import queryClient from '@api/datasource/query'

import QueryKeys from '@constants/queryKeys'
import { ModalFormType, ModalProps } from '@customTypes/page'
import { ToolBaseType } from '@customTypes/tool'

import { useIsMobile, useTableMinWidth } from '@hooks/mobile'

import { ErrorService } from '@utils/error'
import { buildSelectOptions, filterSelect } from '@utils/form'
import { isLoadingMutation, isLoadingOrRefetchQuery } from '@utils/network'

import Input from '@components/shared/input'
import TableCustom, { GenericColumnType } from '@components/shared/table'
import ToolForm from '@components/tool/form'
import ConfirmationModal from '@components/shared/confirmationModal'
import NumberFormat from '@components/shared/Number'
import { LIMIT_SELECT_SEARCH } from '@constants/app'

const INITIAL: CreateWarehouseToolType = {
  name: '',
  description: '',
  tools: [],
  inHouse: false,
}

const WarehouseToolForm = (props: ModalProps<WarehouseToolBaseType>) => {
  const { open, modal, data, size = 'lg', onSubmitted, onClose: outerOnClose } = props

  const isMobile = useIsMobile()

  const minWidth = useTableMinWidth(isMobile, true)

  const [confirmExit, setConfirmExit] = React.useState(false)

  const [selectedRows, handlersSelected] = useListState<string>([])

  const [modalForm, setModalForm] = React.useState<ModalFormType<ToolBaseType>>({
    open: false,
  })

  const formWarehouseTool = useForm<CreateWarehouseToolType>({
    initialValues: INITIAL,
    validate: zodResolver(CreateWarehouseToolSchema),
  })

  const toolsQuery = useQuery<ToolBaseType[], ErrorService, ToolBaseType[]>({
    queryKey: [QueryKeys.GET_TOOLS_KEY],
    queryFn: async () => {
      const response = await DataRepo.constructionEntitiesService.getTools({})

      return response
    },
  })

  const toolCreateMutation = useMutation<
    WarehouseToolBaseType,
    ErrorService,
    CreateWarehouseToolType
  >({
    mutationFn: async (data) => {
      const response = await DataRepo.warehouseEntitiesService.createWarehouseTool(data)

      await queryClient.invalidateQueries({
        predicate: (query) =>
          [QueryKeys.GET_WAREHOUSES_TOOLS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      onSubmitted?.(response)

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al crear la bodega',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Bodega creada correctamente',
      })

      onClose()
    },
  })

  const warehouseToolUpdateMutation = useMutation<void, ErrorService, UpdateWarehouseToolType>({
    mutationFn: async (data) => {
      const response = await DataRepo.warehouseEntitiesService.updateWarehouseTool(data)

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_WAREHOUSE_TOOL_KEY, data.uid],
        refetchType: 'all',
        exact: true,
      })

      onSubmitted?.()

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al actualizar la bodega',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Bodega actualizada correctamente',
      })

      onClose?.()
    },
  })

  React.useEffect(() => {
    if (open && data) {
      formWarehouseTool.setValues(data)
    } else if (!open) {
      formWarehouseTool.setValues(INITIAL)
      handlersSelected.setState([])
    }
    formWarehouseTool.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data])

  const { tools } = formWarehouseTool.getValues()

  const isLoading = isLoadingMutation(toolCreateMutation, warehouseToolUpdateMutation)

  const content = (
    <form
      onSubmit={formWarehouseTool.onSubmit((values) => {
        if (data) {
          warehouseToolUpdateMutation.mutate({
            ...values,
            uid: data.uid,
          } as UpdateWarehouseToolType)
        } else {
          toolCreateMutation.mutate(values as CreateWarehouseToolType)
        }
      })}
    >
      <div className="cd-flex cd-flex-col cd-gap-y-[1rem]">
        <Input
          label="Nombre"
          placeholder="Nombre"
          typeInput="text"
          {...formWarehouseTool.getInputProps('name')}
          onBlur={() => formWarehouseTool.validateField('name')}
        />
        <Input
          autosize
          label="Descripción"
          maxRows={4}
          minRows={2}
          placeholder="Escriba una descripción de la bodega o proyecto"
          typeInput="textarea"
          {...formWarehouseTool.getInputProps('description')}
          onBlur={() => formWarehouseTool.validateField('description')}
        />

        <Input
          label="¿Es una bodega interna?"
          labelPosition="left"
          size="lg"
          typeInput="switch"
          {...formWarehouseTool.getInputProps('inHouse')}
        />

        {!data && (
          <React.Fragment>
            <Input
              allowDeselect
              clearable
              searchable
              className="cd-mt-[1rem]"
              data={buildSelectOptions<ToolBaseType>({
                data: toolsQuery.data?.filter((item) => !tools.some((rm) => rm.uid === item.uid)),
                value: 'uid',
                label: 'name',
              })}
              filter={(input) =>
                filterSelect({
                  ...input,
                  limit: LIMIT_SELECT_SEARCH,
                })
              }
              loading={isLoadingOrRefetchQuery(toolsQuery)}
              nothingFoundMessage={
                <div>
                  <Text c="dimmed" className="cd-text-sm">
                    {toolsQuery.data?.length
                      ? 'No se pueden duplicar herramientas, para cambiar sotck de clic en el icono de editar en la tabla anterior'
                      : 'No hay herramientas disponibles'}
                  </Text>
                  <Button
                    className="cd-mt-[1rem]"
                    color="blue"
                    size="xs"
                    variant="light"
                    onClick={() => setModalForm({ open: true })}
                  >
                    Crear nueva herramienta
                  </Button>
                </div>
              }
              placeholder="Escribe para buscar herramienta existente"
              rightSection={<IconSearch size={18} />}
              typeInput="select"
              value={null}
              onChange={(value) => {
                const tool = toolsQuery.data?.find((item) => item.uid === value)

                if (!tool) return

                formWarehouseTool.insertListItem('tools', {
                  ...tool,
                  stock: 1,
                } as CreateWarehouseToolType['tools'][0])
              }}
            />

            <TableCustom<CreateWarehouseToolType['tools'][0]>
              columns={buildColumns()}
              data={tools}
              keyId="uid"
              limitPage={5}
              minWidth={minWidth}
              noDataMessage="No se han agregado herramientas 2"
              selectedRows={selectedRows}
              onChangeSelected={handlersSelected}
              onDelete={(ids) => {
                const filtered = tools.filter((item) => !ids.includes(item.uid))

                formWarehouseTool.setFieldValue('tools', filtered, { forceUpdate: true })
                handlersSelected.setState([])
              }}
            />
          </React.Fragment>
        )}

        {data && (
          <Text c="dimmed">
            Para cambiar el stock de una herramienta, haga clic en el botón "Ver registros" →
            "Modificar Stock". Cierre el formulario para ver el botón
          </Text>
        )}

        <ToolForm
          modal
          data={modalForm.data}
          open={modalForm.open}
          size="xl"
          onClose={() => setModalForm({ open: false })}
          onSubmitted={(rm) => {
            if (!rm) return

            formWarehouseTool.insertListItem('tools', rm)
          }}
        />

        <Button
          className="cd-mt-[1rem]"
          disabled={!formWarehouseTool.isValid()}
          loaderProps={{ type: 'dots' }}
          loading={isLoading}
          type="submit"
        >
          {data ? 'Guardar bodega' : 'Agregar bodega'}
        </Button>
      </div>
    </form>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title={data ? 'Editar bodega' : 'Agregar bodega'}
        onClose={() => {
          if (formWarehouseTool.isDirty()) {
            setConfirmExit(true)
          } else {
            onClose()
          }
        }}
      >
        {content}
        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={onClose}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el formulario, ¿Deseas cerrarlo y descartar los cambios?
          </Text>
        </ConfirmationModal>
      </Modal>
    )
  }

  return content

  function buildColumns() {
    return [
      { key: 'name', label: 'Nombre', width: '3%', type: 'text' },
      {
        key: 'quantity',
        label: 'Stock',
        width: '2%',
        type: 'number',
        disabled: Boolean(data),
        inputProps: {
          min: 1,
          defaultValue: 1,
          allowNegative: false,
          onChange: handleMaterialInput.bind(null, 'stock'),
        },
      },
      {
        key: 'value',
        label: 'Valor ($)',
        width: '3%',
        type: 'calc',
        align: 'right',
        render: ({ value }) => <NumberFormat value={value} />,
      },
    ] as GenericColumnType<CreateWarehouseToolType['tools'][0]>[]
  }

  function handleMaterialInput(
    key: keyof CreateWarehouseToolType['tools'][0],
    value: number | string,
    uid: string,
  ) {
    const toolIdx = tools.findIndex((item) => item.uid === uid)

    if (toolIdx === -1) return

    formWarehouseTool.setFieldValue(`tools.${toolIdx}.${key}`, value, {
      forceUpdate: true,
    })
  }

  function onClose() {
    outerOnClose?.()
    setConfirmExit(false)
    formWarehouseTool.reset()
  }
}

export default WarehouseToolForm
